import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

/**
 * Completion Trend Chart Component
 * Displays scheduled, completed, and overdue tasks by month
 */
export const CompletionTrendChart = ({ data = [] }) => {
  // Ensure all 12 months are present
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const monthMap = {};
  monthNames.forEach((name, idx) => {
    monthMap[idx + 1] = {
      month: idx + 1,
      month_name: name,
      total_scheduled: 0,
      completed: 0,
      overdue: 0
    };
  });

  data.forEach((item) => {
    if (item.month && monthMap[item.month]) {
      monthMap[item.month] = {
        ...monthMap[item.month],
        total_scheduled: item.total_scheduled || 0,
        completed: item.completed || 0,
        overdue: item.overdue || 0
      };
    }
  });

  const chartData = Object.values(monthMap);

  const customTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{payload[0].payload.month_name}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={350}>
      <BarChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month_name" />
        <YAxis />
        <Tooltip content={customTooltip} />
        <Legend />
        <Bar
          dataKey="total_scheduled"
          fill="#1f77b4"
          name="Scheduled"
          radius={[4, 4, 0, 0]}
        />
        <Bar
          dataKey="completed"
          fill="#2ca02c"
          name="Completed"
          radius={[4, 4, 0, 0]}
        />
        <Bar
          dataKey="overdue"
          fill="#d62728"
          name="Overdue"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Status Mix Donut Chart Component
 * Displays distribution of task statuses
 */
export const StatusMixChart = ({ data = {} }) => {
  const mapping = [
    { key: 'scheduled', label: 'Scheduled', color: '#1f77b4' },
    { key: 'assigned', label: 'Assigned', color: '#ff7f0e' },
    { key: 'inProgress', label: 'In Progress', color: '#9467bd' },
    { key: 'completed', label: 'Completed', color: '#2ca02c' },
    { key: 'cancelled', label: 'Cancelled', color: '#d62728' },
    { key: 'overdue', label: 'Overdue', color: '#e377c2' }
  ];

  const chartData = mapping
    .map((item) => ({
      ...item,
      value: data[item.key] || 0
    }))
    .filter((item) => item.value > 0);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const customTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
      return (
        <div className="chart-tooltip status-mix-tooltip">
          <p className="tooltip-label">{item.payload.label}</p>
          <p style={{ color: item.color }}>
            {item.value} ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    
    // Don't show label for slices < 3%
    if (percent < 0.03) return null;
    
    // Calculate position for label - place it in the middle of the slice
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    // Calculate if we're on the right or left side for text anchor
    const isRightSide = x > cx;
    
    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="14"
        fontWeight="700"
        style={{
          textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
          filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.8))'
        }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (chartData.length === 0) {
    return (
      <div className="chart-empty">
        <p>No status data available</p>
      </div>
    );
  }

  return (
    <div className="status-mix-chart-container">
      <div className="status-mix-chart-wrapper">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={90}
              outerRadius={130}
              paddingAngle={3}
              dataKey="value"
              label={renderCustomLabel}
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
            content={customTooltip} 
            position={{ x: 0, y: 0 }}
            allowEscapeViewBox={{ x: false, y: false }}
            wrapperStyle={{ 
              outline: 'none',
              zIndex: 1000
            }}
          />
          </PieChart>
        </ResponsiveContainer>
        <div className="status-mix-center">
          <div className="center-total">{total}</div>
          <div className="center-label">Total Tasks</div>
        </div>
      </div>
      <div className="status-mix-legend">
        {chartData.map((item, idx) => (
          <div key={idx} className="legend-item">
            <span className="legend-color" style={{ backgroundColor: item.color }}></span>
            <span className="legend-label">{item.label}</span>
            <span className="legend-value">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Chart utilities and shared styles
 */
export const chartStyles = `
  .chart-tooltip {
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  .chart-tooltip .tooltip-label {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .chart-tooltip p {
    margin: 0;
    line-height: 1.4;
  }

  .chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #999;
    font-size: 14px;
  }

  .status-mix-chart-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .status-mix-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    pointer-events: none;
  }

  .status-mix-center .center-total {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
  }

  .status-mix-center .center-label {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
  }

  .status-mix-legend {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 20px;
    width: 100%;
  }

  .status-mix-legend .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
  }

  .status-mix-legend .legend-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .status-mix-legend .legend-label {
    font-weight: 500;
    color: #1a1a1a;
  }

  .status-mix-legend .legend-value {
    color: #999;
    margin-left: auto;
  }

  @media (max-width: 768px) {
    .status-mix-legend {
      grid-template-columns: 1fr;
    }
  }
`;



// WEBPACK FOOTER //
// ./src/shared/components/DashboardCharts/index.js